import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  CalendarCheck,
  UserPlus,
  Sparkles,
  Info,
  Fish,
  Briefcase,
} from "lucide-react";
import { InsufficientPointsErrorData } from "@/types/apiErrors";
import { cn } from "@/lib/utils";
import { SprayIcon } from "@/components/icons/SprayIcon";
import { useCurrentUser } from "@/stores/userStore";

interface InsufficientPointsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorData: InsufficientPointsErrorData | null;
}

const ActionIcon = ({ type }: { type: string }) => {
  switch (type) {
    case "daily_sign_in":
      return (
        <div className="p-2 bg-yellow-500/10 rounded-full">
          <CalendarCheck className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
        </div>
      );
    case "invite_friend":
      return (
        <div className="p-2 bg-blue-500/10 rounded-full">
          <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-500" />
        </div>
      );
    case "add_first_tank":
      return (
        <div className="p-2 bg-cyan-500/10 rounded-full">
          <Fish className="h-5 w-5 text-cyan-600 dark:text-cyan-500" />
        </div>
      );
    case "add_first_tool":
      return (
        <div className="p-2 bg-indigo-500/10 rounded-full">
          <Briefcase className="h-5 w-5 text-indigo-600 dark:text-indigo-500" />
        </div>
      );
    default:
      return (
        <div className="p-2 bg-primary/10 rounded-full">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
      );
  }
};

export const InsufficientPointsDialog: React.FC<
  InsufficientPointsDialogProps
> = ({ open, onOpenChange, errorData }) => {
  const { t } = useTranslation("assessments");
  const currentUser = useCurrentUser();

  if (!errorData) return null;

  // 过滤兜底显示的任务
  const fallbackActions = [
    {
      type: "daily_sign_in",
      points: 10,
      visible: !currentUser?.checked_in,
    },
    { type: "invite_friend", points: 100, visible: true },
    { type: "add_first_tank", points: 50, visible: !currentUser?.has_tanks },
    { type: "add_first_tool", points: 25, visible: !currentUser?.has_tools },
  ].filter((a) => a.visible);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border-none bg-background shadow-2xl">
        {/* 顶部装饰性背景 - 优化黑白双色 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-muted/30 dark:from-muted/10 to-transparent pointer-events-none" />

        <div className="relative p-6 pt-10">
          <DialogHeader className="items-center text-center">
            {/* 中心大图标 - 优化黑白双色 */}
            <div className="mb-4 relative">
              <div className="absolute inset-0 bg-destructive/10 dark:bg-destructive/5 blur-2xl rounded-full" />
              <div className="relative h-16 w-16 bg-gradient-to-br from-destructive/10 to-destructive/5 dark:from-destructive/20 dark:to-destructive/10 rounded-2xl border border-destructive/20 dark:border-destructive/30 flex items-center justify-center rotate-3 hover:rotate-0 transition-transform duration-300 shadow-lg">
                <SprayIcon className="h-8 w-8 text-destructive dark:text-destructive/90 animate-pulse" />
              </div>
            </div>

            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
              {t("errors.insufficientPoints.title")}
            </DialogTitle>

            <DialogDescription className="text-sm text-muted-foreground max-w-[330px] text-center mx-auto mt-2 leading-relaxed">
              {t("errors.insufficientPoints.description", {
                required: errorData.required,
              })}
            </DialogDescription>
          </DialogHeader>

          {/* 差额提示 - 优化黑白双色 */}
          <div className="mt-6 p-4 rounded-xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 dark:border-amber-500/30 flex items-start gap-3">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-500 shrink-0" />
            <p className="text-xs sm:text-xs text-foreground/90 dark:text-foreground/80 leading-snug">
              {t("errors.insufficientPoints.shortageHint", {
                shortage: errorData.shortage,
              })}
            </p>
          </div>

          {/* 获取途径列表 - 优化黑白双色 */}
          <div className="mt-4 space-y-2">
            {errorData.suggested_actions.length > 0
              ? errorData.suggested_actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (action.action_url)
                        window.location.href = action.action_url;
                    }}
                    className="w-full flex items-center justify-between p-3.5 rounded-xl border border-border/50 hover:border-border hover:bg-muted/30 dark:hover:bg-muted/20 transition-all group text-left mb-1 last:mb-0 hover:shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <ActionIcon type={action.type} />
                      <div>
                        <div className="text-sm font-semibold flex items-center gap-2 text-foreground">
                          {t(
                            `errors.insufficientPoints.methods.${action.type}.title` as any,
                            action.title,
                          )}
                          {action.highlight && (
                            <Badge
                              variant="default"
                              className="h-4 px-1.5 text-[10px] bg-primary/80 hover:bg-primary"
                            >
                              HOT
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                          {t(
                            `errors.insufficientPoints.methods.${action.type}.desc` as any,
                            "",
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-primary">
                        +{action.points}
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
                    </div>
                  </button>
                ))
              : // 兜底常用途径
                fallbackActions.map((action) => (
                  <button
                    key={action.type}
                    className="w-full flex items-center justify-between p-3.5 rounded-xl border border-border/50 hover:border-border hover:bg-muted/30 dark:hover:bg-muted/20 transition-all group text-left mb-1 last:mb-0 hover:shadow-sm"
                    onClick={() => {
                      switch (action.type) {
                        case "daily_sign_in":
                          window.location.href = "/profile";
                          break;
                        case "invite_friend":
                          window.location.href = "/app/invitations";
                          break;
                        case "add_first_tank":
                          window.location.href = "/tanks/new";
                          break;
                        case "add_first_tool":
                          window.location.href = "/toolbox";
                          break;
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <ActionIcon type={action.type} />
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {t(
                            `errors.insufficientPoints.methods.${action.type}.title` as any,
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {t(
                            `errors.insufficientPoints.methods.${action.type}.desc` as any,
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-primary">
                        +{action.points}
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
                    </div>
                  </button>
                ))}
          </div>

          {/* 底部备注 */}
          <p className="mt-4 text-[10px] text-center text-muted-foreground/70 dark:text-muted-foreground/50 px-4">
            {t("errors.insufficientPoints.footerNote")}
          </p>
        </div>

        <DialogFooter className="p-4 bg-muted/20 dark:bg-muted/10 border-t border-border/50 flex flex-row gap-3">
          <Button
            variant="ghost"
            className="flex-1 text-xs font-medium hover:bg-muted/50 dark:hover:bg-muted/30"
            onClick={() => onOpenChange(false)}
          >
            {t("errors.insufficientPoints.close")}
          </Button>
          <Button
            className="flex-1 text-xs font-bold shadow-md shadow-primary/10 dark:shadow-primary/5 active:scale-95 transition-transform bg-foreground text-background hover:bg-foreground/90 dark:bg-foreground dark:text-background dark:hover:bg-foreground/90"
            onClick={() => {
              window.location.href = "/profile";
            }}
          >
            {t("errors.insufficientPoints.goEarn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
